import React, { useEffect, useState } from 'react'
import { Button, InputAdornment, Radio, Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { FilterContainer, SortByContainer, ZipcodeInput } from './styles'
import { isValidZipcode } from '../../utils/helpers'

const DEFAULT_ZIPCODE = '92103'
const DEFAULT_LATLNG = [32.745427, -117.17017]

type ZipcodeFilterType = {
  onSearch: (latitude: number, longitude: number) => void
  geocoder?: google.maps.Geocoder
  sortBy: string
  handleSortBy: (sort: string) => void
  disableSortByZipcode?: boolean
  setGettingLocation: (getting: boolean) => void
}

const ZipcodeFilter = ({
  onSearch,
  geocoder,
  sortBy,
  handleSortBy,
  disableSortByZipcode,
  setGettingLocation,
}: ZipcodeFilterType) => {
  const [zipcode, setZipcode] = useState<string>('')
  const [browserLatLng, setBrowserLatLng] = useState<number[]>([0, 0])
  const [browserZipcode, setBrowserZipcode] = useState('')
  const [showError, setShowError] = useState<boolean>(false)

  useEffect(() => {
    if (navigator.geolocation) {
      setGettingLocation(true)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude
          const long = position.coords.longitude
          handleSortBy('zipcode')
          onSearch(lat, long)
          setBrowserLatLng([lat, long])
          const point = new window.google.maps.LatLng(lat, long)
          if (geocoder) {
            geocoder
              .geocode({ location: point }, (results, status) => {
                if (
                  status === window.google.maps.GeocoderStatus.OK &&
                  results
                ) {
                  const zip = results[0].address_components.find(
                    (addr) => addr?.types[0] === 'postal_code'
                  )?.short_name
                  setZipcode(zip || '')
                  setBrowserZipcode(zip || '')
                  setGettingLocation(false)
                } else {
                  setGettingLocation(false)
                  // eslint-disable-next-line no-console
                  console.log('Geocoder request failed: ', status)
                }
              })
              .then(() => {})
              .catch(() => {
                setGettingLocation(false)
                // eslint-disable-next-line no-console
                console.log('Geocoder request failed.')
              })
          }
        },
        () => {
          setGettingLocation(false)
          onSearch(0, 0)
        }
      )
    }
  }, [geocoder, navigator.geolocation])

  const handleSearch = () => {
    if (geocoder) {
      handleSortBy('zipcode')
      geocoder
        .geocode(
          { address: `${zipcode} Southern California USA` },
          (results, status) => {
            if (status === window.google.maps.GeocoderStatus.OK && results) {
              setShowError(false)
              onSearch(
                results[0].geometry.location.lat(),
                results[0].geometry.location.lng()
              )
            } else {
              // eslint-disable-next-line no-console
              console.log('Geocoder request failed: ', status)
            }
          }
        )
        .then(() => {})
        // eslint-disable-next-line no-console
        .catch(() => console.log('Geocoder request failed.'))
    } else {
      setShowError(true)
    }
  }

  const handleOnValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZipcode(e.target.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleTryNextSlot = () => {
    setShowError(false)
    onSearch(0, 0)
    setZipcode('')
    handleSortBy('nextSlot')
  }

  const handleTryZip = () => {
    if (disableSortByZipcode && !browserLatLng[0] && !browserLatLng[1]) {
      onSearch(DEFAULT_LATLNG[0], DEFAULT_LATLNG[1])
      setZipcode(DEFAULT_ZIPCODE)
    } else if (isValidZipcode(zipcode)) {
      handleSearch()
    } else {
      setZipcode(browserZipcode)
      onSearch(browserLatLng[0], browserLatLng[1])
    }
    handleSortBy('zipcode')
    setShowError(false)
  }

  return (
    <FilterContainer>
      <div>
        <ZipcodeInput
          value={zipcode}
          onChange={handleOnValueChange}
          onKeyDown={handleKeyDown}
          variant="outlined"
          size="small"
          placeholder="Search by location or zip code"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
        />
        <Button color="primary" variant="plain" onClick={handleSearch} sx={(theme) => ({ backgroundColor: theme.palette.backgroundMain })}>
          Search
        </Button>
      </div>
      <SortByContainer>
        <Typography color="#777" fontSize={14}>
          Sort by:
        </Typography>
        <Radio
          checked={sortBy === 'zipcode'}
          value="zipcode"
          size="small"
          onChange={handleTryZip}
          inputProps={{ 'aria-label': 'Distance' }}
        />
        <Typography fontSize={14} onClick={handleTryZip}>
          Distance
        </Typography>
        <Radio
          checked={sortBy === 'nextSlot'}
          value="nextSlot"
          size="small"
          onChange={handleTryNextSlot}
          inputProps={{ 'aria-label': 'Availability' }}
        />
        <Typography fontSize={14} onClick={handleTryNextSlot}>
          Availability
        </Typography>
      </SortByContainer>
      <div>
        {showError && (
          <Typography fontSize={14} color="error">
            You must provide a valid zip code or location name
          </Typography>
        )}
      </div>
    </FilterContainer>
  )
}

export default ZipcodeFilter
